<template>
    <hts-drawer
        v-model="visible"
        :submit="handleSubmit"
        :size="80"
        :title="$t('labels.addMaketplaceConfig')"
        @close="close">
        <hts-card :title="$t('labels.generalInformation')">
            <hts-form-select
                v-model="formData.shipmentKey"
                label="Loại hình vận chuyển"
                :items="shipmentTypes"
                key-id="key"
                key-label="label"
                name="shipmentType"
                prop="shipmentType"
                :clearable="false"
                required
                @change="changeShipmentType" />

            <hts-select-work-flow
                v-model="formData.workflow"
                v-model:name="formData.workflowName"
                prop="workflow"
                :transportation="formData.transportation"
                :disabled="!formData.transportation"
                :param-prepare="formData.shipmentKey"
                @change="handelChangeSelectWorkFlow" />

            <div
                v-if="formData.positions.length > 0"
                v-loading="loadingSelectNode"
                class="space-y-4">
                <!-- Tiêu đề -->
                <span class="text-lg font-semibold text-default-700">
                    {{ $t('labels.route') }}
                </span>

                <p>
                    Cài đặt điểm bắt đầu, điểm kết thúc trong chặng của khách hàng
                </p>

                <!-- Danh sách tuyến -->
                <div class="overflow-x-auto pb-2">
                    <div
                        :style="{ minWidth: minWidthScroll }"
                        class="space-y-4">
                        <div
                            v-for="(item, index) in formData.positions"
                            :key="index"
                            :class="['bg-white rounded-xl shadow-sm p-4 transition']">
                            <div class="flex items-center justify-between gap-4">
                                <!-- Tên node -->
                                <div class="text-base font-medium text-gray-700 flex-1">
                                    {{ index + 1 }}. {{ item.nodeTypeName }}
                                </div>

                                <div class="flex gap-2">
                                    <hts-form-button
                                        size="small"
                                        color="info"
                                        type="button"
                                        :outline="formData.selectedStartIndex !== index"
                                        @click="selectStart(index)">
                                        {{ formData.selectedStartIndex === index ? '✅ Bắt đầu' : 'Chọn bắt đầu' }}
                                    </hts-form-button>

                                    <hts-form-button
                                        size="small"
                                        color="success"
                                        type="button"
                                        :outline="formData.selectedEndIndex !== index"
                                        @click="selectEnd(index)">
                                        {{ formData.selectedEndIndex === index ? '✅ Kết thúc' : 'Chọn kết thúc' }}
                                    </hts-form-button>
                                </div>

                                <hts-form-button
                                    icon-name="hi-pencil-square-icon"
                                    size="small"
                                    class="min-w-[12rem]"
                                    color="info"
                                    :loading="updateCompletionTime"
                                    @click="() => openWorkflowPosition(item)">
                                    {{ $t('labels.editCompletionTime') }}
                                </hts-form-button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Kết quả đã chọn -->
                <div
                    v-if="formData.selectedStartIndex !== null && formData.selectedEndIndex !== null"
                    class="text-green-700 font-medium mt-2">
                    Bạn đã chọn từ điểm {{ formData.selectedStartIndex + 1 }} đến điểm {{ formData.selectedEndIndex + 1 }}.
                </div>
            </div>

            <!-- Thiết lập dung sai -->
            <div class="mt-4">
                <span class="text-lg font-semibold text-default-700">
                    Thiết lập dung sai KM
                </span>
                <p>
                    Khoảng chênh lệch quãng đường cho phép giữa khách hàng và thực tế nhà xe.
                </p>

                <hts-form-select
                    v-model="formData.toleranceType"
                    :items="[
                        { label: 'Dung sai cố định (km)', value: 'km' },
                        { label: 'Dung sai theo %', value: 'percent' }
                    ]"
                    label="Loại dung sai"
                    name="toleranceType"
                    prop="toleranceType"
                    :clearable="false" />

                <!-- Dung sai theo km -->
                <hts-form-input
                    v-if="formData.toleranceType === 'km'"
                    v-model="formData.toleranceKm"
                    type="number"
                    label="Dung sai cố định (km)"
                    placeholder="Nhập số km"
                    :min="0" />

                <!-- Dung sai theo % -->
                <hts-form-input
                    v-else-if="formData.toleranceType === 'percent'"
                    v-model="formData.tolerancePercent"
                    type="number"
                    label="Dung sai theo %"
                    placeholder="Nhập phần trăm"
                    :min="0"
                    :max="1000" />
            </div>

            <workflow-position
                ref="workflowPosition"
                @update="(val) => updateWorkflowPosition(val)" />
        </hts-card>
    </hts-drawer>
</template>
<script lang="ts" setup>
import WorkflowPosition from './workflow-position.vue'
import screen from '~/components/utils/screen'
import shipmentTypes from '~/data/select/shipmentAndTransportationTypes.json'
import type { IWorkflowPosition } from '~/pages/admincp/workflow/-imports/form'
const visible = ref<boolean>(false)
const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})
const { $disableReactive } = useNuxtApp()
const emits = defineEmits(['refreshTable'])
const { $t } = useNuxtApp()
const formData = ref<{ [key: string]: any }>({
  shipmentKey: '',
  shipmentTypeName: '',
  transportation: '',
  shipmentType: '',

  selectedStartIndex: null,
  selectedEndIndex: null,

  workflow: '',
  workflowName: '',
  positions: [],

  toleranceType: 'km',
  toleranceKm: 0,
  tolerancePercent: 0
  // commodity: [],
  // typeCommodity: '',
  // typeReefer: '',
  // goodType: '',
  // typeDangerous: '',
  // typeFragileGoods: '',
  // weightCommodity: null,
  // contCategory: '',
  // vehicleCategory: '',
  // vehicleCategoryName: '',
  // quote: '',
  // productivity: null,

  // theSamePositions: []
})

const dataWorkflow = ref<{ [key: string]: any }>({})
const loadingSelectNode = ref<boolean>(false)
const updateCompletionTime = ref(false)
const workflowPosition = ref()

/**
 * Chọn điểm bắt đầu
 */
function selectStart (index: number) {
  formData.value.selectedStartIndex = index
  formData.value.selectedEndIndex = null
}

/**
 * Chọn điểm kết thúc (chỉ cho phép sau điểm bắt đầu)
 */
function selectEnd (index: number) {
  if (formData.value.selectedStartIndex !== null && index > formData.value.selectedStartIndex) {
    formData.value.selectedEndIndex = index
  } else {
    $toast().error('Vui lòng chọn điểm kết thúc nằm sau điểm bắt đầu')
  }
}

function isIndexInRange (index: number): boolean {
  return (
    formData.value.selectedStartIndex !== null &&
    formData.value.selectedEndIndex !== null &&
    index >= formData.value.selectedStartIndex &&
    index <= formData.value.selectedEndIndex
  )
}

const changeShipmentType = (data: any) => {
  data = data?.[0]

  if (!data) {
    return
  }

  Object.assign(formData.value, {
    shipmentTypeName: data.label,
    transportation: data.transportation,
    shipmentType: data.shipmentType,

    workflow: '',
    route: [],
    positions: []
  })
}
const handelChangeSelectWorkFlow = async (val: any) => {
  if (Array.isArray(val) && val.length) {
    const temp = val[0] ?? {}

    dataWorkflow.value = temp
  } else {
    dataWorkflow.value = []
  }
  setDataFromWorkflow()
  await changeCategory()
}
const open = () => {
  visible.value = true
}

const setDataFromWorkflow = () => {
  formData.value.positions = dataWorkflow.value.positions || []
  // formData.value.theSamePositions = dataWorkflow.value.theSamePositions || []
  formData.value.contCategory = ''
  formData.value.vehicleCategory = ''
  formData.value.vehicleCategoryName = ''
}

const changeCategory = async (val?: any) => {
  ///
  await setDataPosition()
}

// gọi api tính thời gian hoàn thành tác vụ của chkd
const setDataPosition = async () => {}

const handelChangeSelectNode = async (indexRoute: number, val: Array<{ [key: string]: any }>) => {
  loadingSelectNode.value = true
  ///
  await changeCategory()
  loadingSelectNode.value = false
}

const openWorkflowPosition = (position: IWorkflowPosition) => {
  if (workflowPosition.value) {
    workflowPosition.value.open($disableReactive(position), dataWorkflow.value.transportation)
  }
}

const updateWorkflowPosition = (position: IWorkflowPosition) => {
  const index = position.index - 1
  formData.value.positions[index] = position
}

const handleSubmit = () => {
  if (formData.value.selectedStartIndex === null || formData.value.selectedEndIndex === null) {
    $toast().error('Vui lòng cài đặt điểm bắt đầu, điểm kết thúc trong chặng của khách hàng')
  }

  const api = {
    method: 'post',
    url: '/marketplace/setting'
  }

  $api(api, { body: formData.value })
    .then((data: any) => {
      const resData = data?.data?.value
      if (resData?.success) {
        process.client && $toast().success($t('labels.addSuccess'))
        fetchTable()
        close()
      }
    })
    .catch(() => {})
}

const dataSelectWorkFlowData = [
  {
    value: 20,
    label: $t('labels.vehicleModule.20ft')
  },
  {
    value: 40,
    label: $t('labels.vehicleModule.40ft')
  },
  {
    value: 45,
    label: $t('labels.vehicleModule.45ft')
  }
]

const fetchTable = () => {
  emits('refreshTable')
}

const close = () => {
  visible.value = false
}

defineExpose({ open })
</script>
